import React, { createContext, useContext, useEffect, useReducer, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import type { UserPublic, UserTeamInfo } from '@arrowise/shared';
import { authService, teamService, ApiError } from '../services';
import { apiClient } from '../utils/apiClient';
import type { AuthState } from '../types';

/**
 * Auth Context Interface
 */
interface AuthContextType extends AuthState {
    // Actions
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    register: (data: {
        email: string;
        password: string;
        firstName: string;
        lastName: string;
        role?: 'user' | 'superuser';
    }) => Promise<void>;
    verifyEmail: (token: string) => Promise<void>;
    resendVerificationEmail: () => Promise<void>;
    forgotPassword: (email: string) => Promise<void>;
    resetPassword: (token: string, password: string) => Promise<void>;
    updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
    updateProfile: (firstName: string, lastName: string) => Promise<void>;
    refreshUser: () => Promise<void>;
    refreshTeams: () => Promise<void>;
    clearError: () => void;
    // Team helpers
    getUserPrimaryTeam: () => UserTeamInfo | null;
    hasTeamAccess: () => boolean;
    getUserTeamRole: (teamId: string) => string | null;
    isTeamOwner: (teamId: string) => boolean;
}

/**
 * Auth Actions
 */
type AuthAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_USER'; payload: UserPublic | null }
    | { type: 'SET_TEAMS'; payload: UserTeamInfo[] }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'SET_AUTHENTICATED'; payload: boolean }
    | { type: 'CLEAR_ERROR' }
    | { type: 'LOGOUT' }
    | { type: 'INITIALIZE'; payload: { user: UserPublic | null; teams: UserTeamInfo[]; isAuthenticated: boolean } };

/**
 * Auth Reducer
 */
function authReducer(state: AuthState, action: AuthAction): AuthState {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };

        case 'SET_USER':
            return {
                ...state,
                user: action.payload,
                isAuthenticated: action.payload !== null,
                error: null
            };

        case 'SET_TEAMS':
            return { ...state, teams: action.payload };

        case 'SET_ERROR':
            return { ...state, error: action.payload, isLoading: false };

        case 'SET_AUTHENTICATED':
            return { ...state, isAuthenticated: action.payload };

        case 'CLEAR_ERROR':
            return { ...state, error: null };

        case 'LOGOUT':
            return {
                user: null,
                teams: [],
                isAuthenticated: false,
                isLoading: false,
                error: null
            };

        case 'INITIALIZE':
            return {
                ...state,
                user: action.payload.user,
                teams: action.payload.teams,
                isAuthenticated: action.payload.isAuthenticated,
                isLoading: false
            };

        default:
            return state;
    }
}

/**
 * Initial auth state
 */
const initialAuthState: AuthState = {
    user: null,
    teams: [],
    isAuthenticated: false,
    isLoading: true,
    error: null,
};

/**
 * Auth Context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
    children: React.ReactNode;
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }: AuthProviderProps) {
    const [state, dispatch] = useReducer(authReducer, initialAuthState);
    const forceLogoutCooldownRef = useRef<number | null>(null);
    const initializationInProgressRef = useRef<boolean>(false);

    /**
     * Set loading state
     */
    const setLoading = useCallback((loading: boolean) => {
        dispatch({ type: 'SET_LOADING', payload: loading });
    }, []);

    /**
     * Set error state
     */
    const setError = useCallback((error: string | null) => {
        dispatch({ type: 'SET_ERROR', payload: error });
    }, []);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        dispatch({ type: 'CLEAR_ERROR' });
    }, []);

    /**
     * Handle API errors
     */
    const handleApiError = useCallback((error: unknown) => {
        if (error instanceof ApiError) {
            // Handle specific error cases
            if (error.isAuthError && error.status === 401) {
                // Don't dispatch LOGOUT here - let the API interceptor handle it
                // This prevents double logout handling
                // console.log('401 error detected, API interceptor will handle logout');
            } else if (error.isRateLimited) {
                toast.error('Too many requests. Please try again shortly.');
                // Do not set global error for rate limit to avoid UI disruptions
            } else if (error.isNetworkError) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(error.message || 'An unexpected error occurred.');
            }
        } else if (error instanceof Error) {
            setError(error.message);
        } else {
            setError('An unexpected error occurred.');
        }
    }, [setError]);

    /**
     * Load user teams data
     */
    const loadUserTeams = async (): Promise<UserTeamInfo[]> => {
        try {
            const result = await teamService.getUserTeams();
            return result.teams;
        } catch (error) {
            console.warn('Failed to load user teams:', error);
            return [];
        }
    };

    /**
 * Initialize auth state on app load
 */
    const initializeAuth = useCallback(async () => {
        // Prevent multiple simultaneous initialization calls
        if (initializationInProgressRef.current) {
            // console.log('Authentication initialization already in progress, skipping...');
            return;
        }

        initializationInProgressRef.current = true;

        // Safety timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
            console.warn('Auth initialization timeout - forcing completion');
            dispatch({
                type: 'INITIALIZE',
                payload: { user: null, teams: [], isAuthenticated: false }
            });
            initializationInProgressRef.current = false;
        }, 10000); // 10 second timeout

        try {
            // Starting authentication initialization...
            dispatch({ type: 'SET_LOADING', payload: true });

            // Cookie-based auth: attempt to fetch current user directly
            const response = await authService.getCurrentUser();

            if (response.success && response.data) {
                // User authenticated, loading teams...
                const teams = await loadUserTeams();
                // Initializing with authenticated user
                dispatch({
                    type: 'INITIALIZE',
                    payload: { user: response.data.user, teams, isAuthenticated: true }
                });
            } else {
                // No authenticated user, initializing as unauthenticated
                dispatch({
                    type: 'INITIALIZE',
                    payload: { user: null, teams: [], isAuthenticated: false }
                });
            }
        } catch (error) {
            // Don't call handleApiError here to avoid double handling
            console.error('Auth initialization error:', error);
            dispatch({
                type: 'INITIALIZE',
                payload: {
                    user: null,
                    teams: [],
                    isAuthenticated: false
                }
            });
        } finally {
            // Clear timeout and reset progress flag
            clearTimeout(timeoutId);
            initializationInProgressRef.current = false;
        }
    }, []);

    /**
     * Login user
     */
    const login = useCallback(async (email: string, password: string) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.login({ email, password });
            if (response.success) {
                // Prefer refetch of /auth/me to ensure cookie session is valid
                const me = await authService.getCurrentUser();
                if (me.success && me.data) {
                    const teams = await loadUserTeams();
                    dispatch({ type: 'SET_USER', payload: me.data.user });
                    dispatch({ type: 'SET_TEAMS', payload: teams });
                } else {
                    throw new Error('Failed to load user after login');
                }
            } else {
                throw new Error(response.message || 'Login failed');
            }
        } catch (error) {
            handleApiError(error);
            throw error; // Re-throw for component handling
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Logout user
     */
    const logout = useCallback(async () => {
        try {
            setLoading(true);
            await authService.logout();
        } catch (error) {
            // Log error but don't prevent logout
            // Don't call handleApiError here to avoid potential double handling
            console.error('Logout error:', error);
        } finally {
            // Ensure both AuthProvider and apiClient are cleared
            dispatch({ type: 'LOGOUT' });
            setLoading(false);
        }
    }, [setLoading]);

    /**
 * Force logout (for API-triggered logout)
 */
    const forceLogout = useCallback(() => {
        const now = Date.now();

        // Prevent repeated force logouts within 3 seconds
        if (forceLogoutCooldownRef.current && now < forceLogoutCooldownRef.current) {
            // console.log('Force logout ignored due to cooldown');
            return;
        }

        // console.log('Force logout triggered by API client');

        dispatch({ type: 'LOGOUT' });
        setError('Your session has expired. Please log in again.');

        // Set cooldown for 3 seconds
        forceLogoutCooldownRef.current = now + 3000;
    }, [setError]);

    /**
     * Register user
     */
    const register = useCallback(async (data: {
        email: string;
        password: string;
        firstName: string;
        lastName: string;
        role?: 'user' | 'superuser';
    }) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.register(data);
            if (!response.success) throw new Error(response.message || 'Registration failed');
            // Attempt automatic login to set cookies
            try {
                await authService.login({ email: data.email, password: data.password });
                const me = await authService.getCurrentUser();
                if (me.success && me.data) {
                    const teams = await loadUserTeams();
                    dispatch({ type: 'SET_USER', payload: me.data.user });
                    dispatch({ type: 'SET_TEAMS', payload: teams });
                }
            } catch (loginError) {
                console.warn('Auto-login after registration failed:', loginError);
            }
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Verify email
     */
    const verifyEmail = useCallback(async (token: string) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.verifyEmail({ token });

            if (response.success && response.data) {
                // Load user teams after email verification
                const teams = await loadUserTeams();

                // Update both user and teams in a single dispatch to prevent multiple re-renders
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        user: response.data.user,
                        teams,
                        isAuthenticated: true
                    }
                });
            } else {
                throw new Error(response.message || 'Email verification failed');
            }
        } catch (error) {
            // Don't call handleApiError for verification errors to avoid duplicate error handling
            // The component will handle the error display
            console.error('Email verification error:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError]);

    /**
     * Resend verification email
     */
    const resendVerificationEmail = useCallback(async () => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.resendVerificationEmail();

            if (!response.success) {
                throw new Error(response.message || 'Failed to resend verification email');
            }
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Forgot password
     */
    const forgotPassword = useCallback(async (email: string) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.forgotPassword({ email });

            if (!response.success) {
                throw new Error(response.message || 'Failed to send password reset email');
            }
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Reset password
     */
    const resetPassword = useCallback(async (token: string, password: string) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.resetPassword({ token, newPassword: password });

            if (!response.success) {
                throw new Error(response.message || 'Password reset failed');
            }
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Update password
     */
    const updatePassword = useCallback(async (currentPassword: string, newPassword: string) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.updatePassword({
                currentPassword,
                newPassword
            });

            if (!response.success) {
                throw new Error(response.message || 'Password update failed');
            }
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Update profile
     */
    const updateProfile = useCallback(async (firstName: string, lastName: string) => {
        try {
            setLoading(true);
            clearError();

            const response = await authService.updateProfile({ firstName, lastName });

            if (response.success && response.data) {
                dispatch({ type: 'SET_USER', payload: response.data.user });
            } else {
                throw new Error(response.message || 'Profile update failed');
            }
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, clearError, handleApiError]);

    /**
     * Refresh user data
     */
    const refreshUser = useCallback(async () => {
        try {
            if (!state.isAuthenticated) return;

            // Don't refresh if user is already verified and we're in the middle of verification
            if (state.user?.emailVerified && state.isAuthenticated) {
                return;
            }

            setLoading(true);
            const [userResponse, teams] = await Promise.all([
                authService.getCurrentUser(),
                loadUserTeams()
            ]);

            if (userResponse.success && userResponse.data) {
                // Update both user and teams in a single dispatch to prevent multiple re-renders
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        user: userResponse.data.user,
                        teams,
                        isAuthenticated: true
                    }
                });
            } else {
                console.warn('User data refresh failed:', userResponse);
            }
        } catch (error) {
            console.error('User data refresh error:', error);
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }, [state.isAuthenticated, state.user?.emailVerified, setLoading, handleApiError]);

    /**
     * Refresh teams data
     */
    const refreshTeams = useCallback(async () => {
        try {
            if (!state.isAuthenticated) return;

            const teams = await loadUserTeams();
            dispatch({ type: 'SET_TEAMS', payload: teams });
        } catch (error) {
            console.warn('Failed to refresh teams:', error);
        }
    }, [state.isAuthenticated]);

    /**
     * Get user's primary team (first team they're a member of)
     */
    const getUserPrimaryTeam = useCallback((): UserTeamInfo | null => {
        return state.teams.length > 0 ? state.teams[0] : null;
    }, [state.teams]);

    /**
     * Check if user has team access
     */
    const hasTeamAccess = useCallback((): boolean => {
        return state.teams.length > 0;
    }, [state.teams]);

    /**
     * Get user's team role for a specific team
     */
    const getUserTeamRole = useCallback((teamId: string): string | null => {
        const teamInfo = state.teams.find(t => t.team.id === teamId);
        return teamInfo?.role || null;
    }, [state.teams]);

    /**
     * Check if user is team owner
     */
    const isTeamOwner = useCallback((teamId: string): boolean => {
        const teamInfo = state.teams.find(t => t.team.id === teamId);
        return teamInfo?.isOwner || false;
    }, [state.teams]);

    /**
     * Initialize auth on component mount
     */
    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    /**
     * Set up logout callback for API client
     */
    useEffect(() => {
        apiClient.setLogoutCallback(forceLogout);

        // Cleanup function to prevent stale callbacks
        return () => {
            apiClient.setLogoutCallback(() => { });
        };
    }, [forceLogout]);

    // Set up rate limit callback to show a toast instead of logging out
    useEffect(() => {
        apiClient.setRateLimitCallback(({ message }) => {
            toast.error(message || 'Too many requests. Please try again shortly.');
        });
    }, []);

    /**
     * Context value
     */
    const contextValue: AuthContextType = {
        ...state,
        login,
        logout,
        register,
        verifyEmail,
        resendVerificationEmail,
        forgotPassword,
        resetPassword,
        updatePassword,
        updateProfile,
        refreshUser,
        refreshTeams,
        clearError,
        getUserPrimaryTeam,
        hasTeamAccess,
        getUserTeamRole,
        isTeamOwner,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}

/**
 * Hook to check if user has specific role
 */
export function useRequireAuth(requiredRole?: 'superuser') {
    const { user, isAuthenticated } = useAuth();

    const hasAccess = isAuthenticated && user && (
        !requiredRole || user.role === requiredRole
    );

    return { hasAccess, user, isAuthenticated };
}

/**
 * Hook to check if user's email is verified
 */
export function useRequireEmailVerification() {
    const { user, isAuthenticated } = useAuth();

    const isEmailVerified = isAuthenticated && user?.emailVerified;

    return { isEmailVerified, user, isAuthenticated };
}
